import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { X, HelpCircle, Search, ChevronRight, ThumbsUp, ThumbsDown, Expand, Loader2, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useKnowledgeArticles, useKnowledgeArticle, useKnowledgeFeedback } from '@/hooks/use-knowledge';

// Category definitions
const CATEGORIES: Array<{ key: string; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'getting-started', label: 'Getting Started' },
  { key: 'projects', label: 'Projects' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'tickets', label: 'Tickets' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'quotes', label: 'Quotes' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'time-tracking', label: 'Time Tracking' },
  { key: 'reports', label: 'Reports' },
  { key: 'account', label: 'Account' },
  { key: 'clients', label: 'Clients' },
  { key: 'leads', label: 'Leads' },
  { key: 'site-monitoring', label: 'Site Monitoring' },
  { key: 'settings', label: 'Settings' },
];

interface KnowledgeDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function KnowledgeDrawer({ open, onClose }: KnowledgeDrawerProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  // Auto-open article from URL param
  useEffect(() => {
    const articleSlug = searchParams.get('article');
    if (articleSlug) {
      setSelectedSlug(articleSlug);
    }
  }, [searchParams]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: articles, isLoading } = useKnowledgeArticles(
    selectedCategory,
    debouncedSearch
  );

  const { data: articleDetail } = useKnowledgeArticle(selectedSlug || undefined);
  const feedbackMut = useKnowledgeFeedback();

  const handleSelectArticle = useCallback((slug: string) => {
    setSelectedSlug(slug);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedSlug(null);
  }, []);

  const handleFeedback = useCallback(
    (helpful: boolean) => {
      if (!selectedSlug) return;
      feedbackMut.mutate({ slug: selectedSlug, helpful });
    },
    [selectedSlug, feedbackMut]
  );

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/50 transition-opacity"
        onClick={onClose}
      />
      {/* Drawer */}
      <div
        className={cn(
          'fixed right-0 top-0 z-50 h-full w-full max-w-[460px] bg-[var(--bg-primary)] shadow-2xl',
          'flex flex-col animate-slide-in-right',
          'border-l border-[var(--border-color)]'
        )}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[var(--border-color)] shrink-0">
          <HelpCircle size={20} className="text-dyndev-green" />
          <h2 className="text-base font-semibold flex-1">
            {selectedSlug ? articleDetail?.title || 'Loading...' : t('kb.title', 'Help & Guides')}
          </h2>
          {!selectedSlug && (
            <a
              href="/help"
              className="p-1.5 rounded-md hover:bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
              title={t('kb.expand', 'Open full page')}
              onClick={(e) => {
                e.preventDefault();
                window.open('/help', '_self');
              }}
            >
              <Expand size={16} />
            </a>
          )}
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {selectedSlug ? (
          // ── Article Detail View ──────────────────────────────────
          <div className="flex-1 overflow-y-auto">
            {!articleDetail ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 size={20} className="animate-spin text-dyndev-green" />
              </div>
            ) : (
              <div className="p-5 space-y-5">
                {/* Back button */}
                <button
                  onClick={handleBack}
                  className="flex items-center gap-1 text-sm text-dyndev-green hover:underline"
                >
                  <ChevronRight size={14} className="rotate-180" />
                  Back to articles
                </button>

                {/* Category badge */}
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--bg-secondary)] text-[var(--text-secondary)]">
                    {CATEGORIES.find((c) => c.key === articleDetail.category)?.label || articleDetail.category}
                  </span>
                </div>

                {/* Content */}
                <div
                  className="prose prose-sm max-w-none text-[var(--text-primary)] [&_h2]:text-lg [&_h2]:font-bold [&_h3]:text-base [&_h3]:font-semibold [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_a]:text-dyndev-green [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-dyndev-green/30 [&_blockquote]:pl-3 [&_blockquote]:text-[var(--text-secondary)]"
                  dangerouslySetInnerHTML={{ __html: articleDetail.content }}
                />

                {/* Feedback */}
                <div className="border-t border-[var(--border-color)] pt-4 mt-6">
                  <p className="text-sm text-[var(--text-secondary)] mb-3">
                    {t('kb.wasThisHelpful', 'Was this article helpful?')}
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleFeedback(true)}
                      disabled={feedbackMut.isPending}
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors',
                        articleDetail.userFeedback === true
                          ? 'bg-dyndev-green/15 text-dyndev-green'
                          : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                      )}
                    >
                      <ThumbsUp size={14} />
                      {articleDetail.helpfulCount > 0 && articleDetail.helpfulCount}
                    </button>
                    <button
                      onClick={() => handleFeedback(false)}
                      disabled={feedbackMut.isPending}
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors',
                        articleDetail.userFeedback === false
                          ? 'bg-red-500/15 text-red-400'
                          : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                      )}
                    >
                      <ThumbsDown size={14} />
                      {articleDetail.notHelpfulCount > 0 && articleDetail.notHelpfulCount}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          // ── Article List View ────────────────────────────────────
          <>
            {/* Search */}
            <div className="px-4 py-3 border-b border-[var(--border-color)] shrink-0">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                <input
                  type="text"
                  placeholder={t('kb.search', 'Search articles...')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 rounded-md bg-[var(--bg-secondary)] border border-[var(--border-color)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-dyndev-green focus:ring-1 focus:ring-dyndev-green"
                />
              </div>
            </div>

            {/* Category tabs */}
            <div className="px-4 py-2 border-b border-[var(--border-color)] shrink-0 overflow-x-auto">
              <div className="flex gap-1.5">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.key}
                    onClick={() => setSelectedCategory(cat.key)}
                    className={cn(
                      'px-2.5 py-1 rounded-md text-xs whitespace-nowrap transition-colors',
                      selectedCategory === cat.key
                        ? 'bg-dyndev-green/15 text-dyndev-green font-medium'
                        : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]'
                    )}
                  >
                    {cat.key === 'all' ? t('kb.allCategories', 'All Categories') : cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Article list */}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 size={20} className="animate-spin text-dyndev-green" />
                </div>
              ) : !articles?.length ? (
                <div className="flex flex-col items-center justify-center h-48 px-4 text-center">
                  <BookOpen size={32} className="text-[var(--text-secondary)] mb-3 opacity-40" />
                  <p className="text-sm text-[var(--text-secondary)] font-medium">
                    {t('kb.noResults', 'No articles found')}
                  </p>
                  <p className="text-xs text-[var(--text-secondary)] mt-1">
                    {t('kb.noResultsDesc', 'Try a different search term or browse the categories above.')}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-[var(--border-color)]">
                  {articles.map((article) => (
                    <button
                      key={article.id}
                      onClick={() => handleSelectArticle(article.slug)}
                      className="w-full text-left px-5 py-4 hover:bg-[var(--bg-secondary)] transition-colors group"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--bg-secondary)] text-[var(--text-secondary)]">
                              {CATEGORIES.find((c) => c.key === article.category)?.label || article.category}
                            </span>
                          </div>
                          <h3 className="text-sm font-medium text-[var(--text-primary)] group-hover:text-dyndev-green transition-colors truncate">
                            {article.title}
                          </h3>
                          {article.excerpt && (
                            <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-2">
                              {article.excerpt}
                            </p>
                          )}
                        </div>
                        <ChevronRight size={14} className="text-[var(--text-secondary)] mt-1 shrink-0" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
